import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

import { VERIFY_ENDPOINT } from "./cards/constants";
import { LoadingCard } from "./cards/LoadingCard";
import { ErrorCard } from "./cards/ErrorCard";
import { SlipSummaryCard } from "./cards/SlipSummaryCard";
import { LegBlock } from "./cards/LegBlock";

// AnalyzerDataCheck is the orchestrator for the "verify-slip" flow.
// It owns exactly two things:
//   1. POST /api/analyzer/verify-slip and manage its lifecycle state
//   2. dispatch the returned {legs, summary} envelope to presentation
//      cards in ./cards/
//
// Per-leg presentation lives in `LegBlock`. Slip Summary lives in
// `SlipSummaryCard`. Nothing in this file should re-implement scoring,
// historical math, market matching, or copy that lives inside a card.
//
// The critical distinction between UNSUPPORTED_MARKET and
// SUPPORTED-WITH-NO-CURRENT-MATCH is preserved by `LegBlock` -- do NOT
// collapse those branches here or anywhere else.
export function AnalyzerDataCheck({ slip, active }) {
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!active || !slip) return;
    let cancelled = false;
    setState({ status: "loading" });

    (async () => {
      try {
        const res = await fetch(VERIFY_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(slip),
        });
        if (!res.ok) {
          if (cancelled) return;
          setState({
            status: res.status === 429 ? "rate_limited" : "provider_unavailable",
            message:
              res.status === 429
                ? "PA is receiving a lot of sports-data requests right now. Please try again shortly."
                : "Current sports data is temporarily unavailable. Please try again.",
          });
          return;
        }
        const body = await res.json();
        if (cancelled) return;

        if (body.ok === false) {
          setState({
            status: body.reason || "provider_unavailable",
            message: body.message || "Sports data is temporarily unavailable.",
          });
          return;
        }
        // Multi-Leg envelope: {legs: [...], summary: {...}}
        const legs = Array.isArray(body.legs) ? body.legs : [];
        const summary = body.summary || null;
        if (!legs.length) {
          setState({
            status: "provider_unavailable",
            message: "No legs on this slip. Please review and try again.",
          });
          return;
        }
        setState({ status: "matched", legs, summary });
      } catch {
        if (cancelled) return;
        setState({
          status: "provider_unavailable",
          message: "Current sports data is temporarily unavailable. Please try again.",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, slip]);

  if (!active) return null;
  const s = state.status;

  if (s === "idle" || s === "loading") return <LoadingCard />;

  if (s === "matched") {
    return (
      <>
        <SlipSummaryCard summary={state.summary} />
        {state.legs.map((leg, idx) => (
          <LegBlock key={leg.id || `leg-${idx}`} leg={leg} />
        ))}
      </>
    );
  }

  if (s === "rate_limited") {
    return (
      <ErrorCard
        testid="analyzer-data-check-rate-limited"
        icon={WifiOff}
        title="Please try again shortly"
        body={
          state.message ||
          "PA is receiving a lot of sports-data requests right now. Please try again shortly."
        }
      />
    );
  }

  // provider_unavailable / any other error at the top level
  return (
    <ErrorCard
      testid="analyzer-data-check-unavailable"
      icon={WifiOff}
      title="Sports data unavailable"
      body={
        state.message ||
        "Current sports data is temporarily unavailable. Please try again."
      }
    />
  );
}
